import React from "react";
// import { useDispatch } from "react-redux";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Typography } from "@material-ui/core";

// import { showNotification } from "jazasoft";
import { required, minValue, maxValue } from "@jazasoft/mui-table";
import { SweetAlert } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";
import useDeepMemo from "../../../../hooks/useDeepMemo";
import { isEmpty } from "../../../../utils/helpers";
import { findSizeBreakupList } from "../../../../helpers/orderHelper";
import { getGroupName } from "../../../grouping/groupingHelper";
import { MarkerType } from "../../../../utils/types";
import { DEFAULT_SEPARATOR } from "../../../../constants";
import { useSafeState } from "../../../../hooks";

const parseRows = (rows, sbList, styleColorList) => {
  const markers = rows.map((m, idx) => {
    const sizeRatioList = sbList
      .map(({ sizeKey, size, sizeGroup, serialNo }, idx) => ({
        ...(!isEmpty(m.sizeRatioList) ? m.sizeRatioList.find(sr => sr.sizeKey === sizeKey) : {}),
        serialNo,
        size,
        sizeGroup,
        sizeKey,
        ratio: m[sizeKey]
      }))
      .filter(sr => sr.ratio && parseFloat(sr.ratio) > 0);
    let markerPlyList = [];
    if (isEmpty(m.markerPlyList)) {
      markerPlyList = styleColorList.map((styleColor, idx) => ({
        style: styleColor.split(DEFAULT_SEPARATOR)[0],
        color: styleColor.split(DEFAULT_SEPARATOR)[1],
        plannedPlies: m[`plies-${idx}`],
        actualPlies: m[`plies-${idx}`]
      }));
    } else {
      markerPlyList = m.markerPlyList
        .sort((a, b) => (a.styleColor < b.styleColor ? -1 : 1))
        .map((mp, idx) => ({
          ...mp,
          plannedPlies: m[`plies-${idx}`],
          actualPlies: m[`plies-${idx}`]
        }));
    }
    const name = sizeRatioList.map(sr => `${sr.size}:${sr.ratio}`).join(", ");
    const noOfWays = sizeRatioList.map(sr => parseInt(sr.ratio)).reduce((acc, r) => acc + r, 0);
    return { ...m, markerNo: idx + 1, name, sizeRatioList, noOfWays, markerPlyList };
  });

  return markers;
};

const ManualMarkerDialog = ({ open, order, orderFlow, onSubmit, onClose }) => {
  const [alertState, setAlertState] = useSafeState({ alertMsgList: [], markers: [] });

  const styleCount = orderFlow?.styleList?.length;
  const colorCount = orderFlow?.colorList?.length;
  const styleColorList = orderFlow?.styleColorList?.sort((a, b) => (a < b ? -1 : 1)) || [];

  const sizeBreakupList = findSizeBreakupList(orderFlow, order);

  const columns = useDeepMemo(() => {
    const choicesGroup =
      orderFlow?.groupList
        ?.filter(g => g.type === "FINAL")
        .sort((a, b) => (a.name < b.name ? -1 : 1))
        .map(e => ({ ...e, name: getGroupName(e) })) || [];

    const sizeList = sizeBreakupList.map((sb, idx) => {
      return {
        dataKey: sb.sizeKey,
        title: sb.size,
        inputType: "text-input",
        options: { type: "number", style: { minWidth: 95 } },
        validate: [minValue(0), maxValue(20)],
        align: "center"
      };
    });

    let columnList = [
      ...sizeList,
      ...styleColorList.map((styleColor, idx) => ({
        dataKey: `plies-${idx}`,
        title:
          styleCount > 1
            ? `Plies - ${styleColor.split(DEFAULT_SEPARATOR)[0]}`
            : colorCount > 1
            ? `Plies - ${styleColor.split(DEFAULT_SEPARATOR)[1]}`
            : "Plies",
        inputType: "text-input",
        options: { type: "number", style: { minWidth: 95 } },
        validate: [required(), minValue(0)]
      })),
      {
        dataKey: "length",
        title: "Length",
        inputType: "text-input",
        options: { type: "number", style: { minWidth: 95 } },
        validate: [minValue(0)]
      },
      {
        dataKey: "efficiency",
        title: "Efficiency",
        inputType: "text-input",
        options: { type: "number", style: { minWidth: 95 } },
        validate: [minValue(0), maxValue(100)]
      },
      {
        dataKey: "groupId",
        title: "Group",
        inputType: "select-input",
        choices: choicesGroup,
        options: { style: { minWidth: 250 } },
        validate: [required()]
      }
    ];
    return columnList;
  }, [order, orderFlow]);

  const rows = useDeepMemo(() => {
    if (isEmpty(orderFlow?.markerList)) return [{}, {}];
    let rowList = orderFlow.markerList
      .filter(m => m.markerType === MarkerType.MANUAL)
      .sort((a, b) => a.markerNo - b.markerNo)
      .map(marker => {
        return {
          ...marker,
          ...marker.sizeRatioList?.reduce((acc, sr) => ({ ...acc, [sr.sizeKey]: sr.ratio }), {}),
          ...marker.markerPlyList
            ?.sort((a, b) => (a.styleColor < b.styleColor ? -1 : 1))
            .reduce((acc, mp, idx) => ({ ...acc, [`plies-${idx}`]: mp.plannedPlies }), {}),
          groupId: marker.group?.id
        };
      });
    return rowList;
  }, [orderFlow]);

  const onValidate = values => {
    const markers = parseRows(values.rows, sizeBreakupList, styleColorList);

    const errors = markers.map(m => {
      let error = {};
      if (m.noOfWays <= 0) {
        error[sizeBreakupList[0]?.sizeKey] = "Required";
      }

      styleColorList.forEach((sc, idx) => {
        const key = `plies-${idx}`;
        const value = m[`plies-${idx}`];
        if (isEmpty(m[key])) {
          error[key] = "Required.";
        } else if (value < 0) {
          error[key] = "Must be positive value";
        }
      });

      if (isEmpty(m.length) && !isEmpty(m.efficiency)) {
        error.length = "Required";
      }
      if (!isEmpty(m.length) && parseFloat(m.length) < 0) {
        error.length = "Must be positive value";
      }
      if (!isEmpty(m.length) && isEmpty(m.efficiency)) {
        error.efficiency = "Required";
      }
      if (!isEmpty(m.efficiency) && parseFloat(m.efficiency) < 0) {
        error.efficiency = "Must be positive value";
      }
      if (!isEmpty(m.efficiency) && parseFloat(m.efficiency) > 100) {
        error.efficiency = "Must be less than 100";
      }
      if (isEmpty(m.groupId)) {
        error.groupId = "Required";
      }

      //Ratio validation //
      sizeBreakupList.forEach((sb, idx) => {
        if (m[sb?.sizeKey] < 0) {
          error[sb?.sizeKey] = "Must be at least 0";
        } else if (m[sb?.sizeKey] > 20) {
          error[sb?.sizeKey] = "Must be 20 or less";
        }
      });

      return error;
    });
    const error = { rows: errors };
    return error;
  };

  const handleSubmit = (values, form, onActionComplete) => {
    const markers = parseRows(values, sizeBreakupList, styleColorList);

    // //** Validation: Way must be greater than 0 *//
    // if (markers.some(m => m.noOfWays <= 0)) {
    //   const errors = markers.map(m => {
    //     let error = {};
    //     if (m.noOfWays <= 0) {
    //       error[sbList[0]?.size] = "Required";
    //     }
    //     return error;
    //   });

    //   const error = { rows: errors };

    //   dispatch(showNotification("Marker Ratio in at least 1 size is required.", { type: "danger" }));
    //   return error;
    // }

    let alertMessageList = [];
    const bomCuList = JSON.parse(orderFlow.bomCu || "[]");
    if (!isEmpty(bomCuList)) {
      const orderingCuSum = bomCuList.reduce((acc, p) => acc + p.bomCu, 0);
      const avgOrderingCu = orderingCuSum / bomCuList.length;

      markers.forEach(row => {
        if (row.efficiency && (row.efficiency < 50 || row.efficiency > 100)) {
          alertMessageList.push(`Marker - '${row.name}' efficiency should be in range 50 to 100`);
        }
        if (row.length && row.length > 1.25 * row.noOfWays * avgOrderingCu) {
          alertMessageList.push(`Marker - '${row.name}' Lay length seems too high`);
        }
        if (row.length && row.length < 0.75 * row.noOfWays * avgOrderingCu) {
          alertMessageList.push(`Marker - '${row.name}' Lay length seems too low`);
        }
      });

      if (!isEmpty(alertMessageList)) {
        setAlertState(prev => ({ ...prev, alertMsgList: alertMessageList, markers: markers }));
        return;
      }
    }

    onSubmit && onSubmit(markers, onActionComplete);
  };

  const onCancel = () => {
    setAlertState(prev => ({ ...prev, alertMsgList: [], markers: [] }));
  };

  const onAlertSubmit = () => {
    onCancel();
    onSubmit && onSubmit(alertState?.markers);
  };

  const onFooterActionClick = (event, action, rows) => {
    if (action === "cancel") {
      onClose && onClose();
    }
  };

  return (
    <div>
      {alertState?.alertMsgList && alertState?.alertMsgList?.length > 0 && (
        <SweetAlert warning={true} title="Warning" onConfirm={onAlertSubmit} onCancel={onCancel} focusCancelBtn showCancel confirmBtnText={"Proceed"}>
          <div style={{ textAlign: "left" }}>
            {alertState?.alertMsgList?.map((msg, idx) => (
              <Typography key={idx}>{msg}</Typography>
            ))}
          </div>
        </SweetAlert>
      )}
      <Dialog id="adjust-dialog" fullWidth={true} maxWidth="lg" open={open} onClose={onClose}>
        <DialogTitle id="import-dialog-title">
          Manual Markers
          <span style={{ marginLeft: "3em", color: "red" }}>Note: Enter Marker Ratio, Not Quantity</span>
        </DialogTitle>
        <MuiTable
          columns={columns}
          rows={rows}
          fontSize={16}
          editable
          editing
          showEditableActions
          actionPlacement="left"
          variant="excel"
          validate={onValidate}
          onSubmit={handleSubmit}
          onFooterActionClick={onFooterActionClick}
        />
      </Dialog>
    </div>
  );
};

export default ManualMarkerDialog;
